import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, Clock, CheckCircle, AlertCircle, Package, Eye, Filter, Search } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAccessibleLocations } from '@/hooks/useAccessibleLocations';
import type { Transfer } from '@/types/location.types';
import type { TransferFilters } from '../hooks/useDistribution';

interface TransfersListProps {
  transfers: Transfer[];
  loading: boolean;
  isCajero?: boolean;
  filters?: TransferFilters;
  onFilterChange?: (updates: Partial<TransferFilters>) => void;
  onSearch?: (filtersOverride?: Partial<TransferFilters>) => void;
}

const statusFilters = [
  { key: '', label: 'Todas', activeBg: 'bg-gray-700 text-white', borderColor: 'border-l-gray-400', countColor: 'text-gray-500' },
  { key: 'pending', label: 'Pendientes', activeBg: 'bg-amber-500 text-white', borderColor: 'border-l-amber-500', countColor: 'text-amber-600' },
  { key: 'in_transit', label: 'En Transito', activeBg: 'bg-sky-500 text-white', borderColor: 'border-l-sky-500', countColor: 'text-sky-600' },
  { key: 'received', label: 'Recibidas', activeBg: 'bg-emerald-500 text-white', borderColor: 'border-l-emerald-500', countColor: 'text-emerald-600' },
  { key: 'cancelled', label: 'Canceladas', activeBg: 'bg-rose-500 text-white', borderColor: 'border-l-rose-500', countColor: 'text-rose-600' },
];

export const TransfersList: React.FC<TransfersListProps> = ({
  transfers,
  loading,
  isCajero = false,
  filters,
  onFilterChange,
  onSearch
}) => {
  const navigate = useNavigate();
  const { locations } = useAccessibleLocations();

  const getStatusBadge = (status: Transfer['status']) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_transit: 'bg-blue-100 text-blue-800',
      received: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };

    const labels = {
      pending: 'Pendiente',
      in_transit: 'En Transito',
      received: 'Recibido',
      cancelled: 'Cancelada'
    };

    return (
      <Badge className={styles[status]}>
        {labels[status]}
      </Badge>
    );
  };

  const getStatusCount = (statusKey: string) => {
    if (!statusKey) return transfers.length;
    return transfers.filter(t => t.status === statusKey).length;
  };

  const handleStatusFilterChange = (statusKey: string) => {
    if (onFilterChange) {
      onFilterChange({ status: statusKey });
    }
    // Pasar el status directamente para evitar problemas de timing con setState
    if (onSearch) {
      onSearch({ status: statusKey });
    }
  };


  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <div className="text-center">
            <Truck className="w-12 h-12 text-gray-400 mx-auto mb-4 animate-pulse" />
            <p className="text-gray-500">Cargando transferencias...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filtros - ocultos para CAJERO */}
      {!isCajero && filters && onFilterChange && (
        <Card>
          <CardContent className="pt-4 pb-3 space-y-2">
            {/* Fila 1: Filtros de búsqueda */}
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Desde:</label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => onFilterChange({ dateFrom: e.target.value })}
                  className="text-sm h-9 w-40"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Hasta:</label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => onFilterChange({ dateTo: e.target.value })}
                  className="text-sm h-9 w-40"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Destino:</label>
                <Select
                  value={filters.locationId || "all"}
                  onValueChange={(value) => onFilterChange({ locationId: value === "all" ? "" : value })}
                >
                  <SelectTrigger className="h-9 w-48 text-sm">
                    <SelectValue placeholder="Todos los locales" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los locales</SelectItem>
                    {locations.map(location => (
                      <SelectItem key={location.id} value={location.id.toString()}>
                        {location.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                size="sm"
                className="h-9 bg-macartur-primary hover:bg-macartur-primary/90"
                onClick={() => onSearch?.()}
              >
                <Search className="mr-1 h-4 w-4" />
                Buscar
              </Button>
            </div>
            {/* Fila 2: Filtro por estado */}
            <div className="flex flex-wrap items-center gap-1.5">
              {statusFilters.map((sf) => {
                const count = getStatusCount(sf.key);
                const isActive = (filters.status || '') === sf.key;
                return (
                  <button
                    key={sf.key}
                    onClick={() => handleStatusFilterChange(sf.key)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      isActive
                        ? sf.activeBg
                        : `bg-white border border-gray-200 border-l-[3px] ${sf.borderColor} text-gray-700 hover:bg-gray-50`
                    }`}
                  >
                    {sf.label}
                    <span className={`text-xs font-bold ${isActive ? 'text-white' : sf.countColor}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lista de Transferencias */}
      {transfers.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Package className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No hay transferencias
            </h3>
            <p className="text-gray-500">
              No se encontraron transferencias en el sistema.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Transferencias ({transfers.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {transfers.map((transfer) => (
                <div
                  key={transfer.id}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  {/* Fila 1: Numero + Destino badge + Estado + Fecha */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-lg">{transfer.transferNumber}</span>
                      <Badge className="text-xs bg-slate-600 hover:bg-slate-600 text-white">
                        {transfer.toLocation?.name}
                      </Badge>
                      {getStatusBadge(transfer.status)}
                    </div>
                    <span className="text-xs text-gray-500">
                      {transfer.requestedAt ? new Date(transfer.requestedAt).toLocaleDateString('es-PE', {
                        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      }) : '-'}
                    </span>
                  </div>

                  {/* Fila 2: Info + Botones */}
                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
                      <span><span className="font-bold">Origen:</span> {transfer.fromLocation?.name}</span>
                      <span><span className="font-bold">Items:</span> {transfer.items.length}</span>
                      {transfer.status === 'in_transit' && transfer.shippedAt && (
                        <span><span className="font-bold">Enviada:</span> {new Date(transfer.shippedAt).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/distribution/transfers/${transfer.id}`)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Ver
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};